/* ============================================================================
   AKINOD — Points de vente
   ----------------------------------------------------------------------------
   Filtrage client de la liste des revendeurs : un champ texte (ville, code
   postal ou enseigne) et un sélecteur de pays. On masque, on ne recharge pas.

   Sans ce fichier, la page reste entièrement utilisable : la première page des
   revendeurs est affichée, la pagination du serveur mène aux suivantes, et le
   formulaire ne fait rien.

   Comparaison insensible aux accents et à la ponctuation : « Genève » se
   trouve en tapant « geneve », « 75005 » comme « 75 005 ».

   ── Recollage des pages ─────────────────────────────────────────────────────
   Liquid ne peut pas rendre plus de 250 métaobjets d'un coup : la liste arrive
   donc paginée. Un filtre qui ne verrait qu'une page sur cinq serait pire que
   pas de filtre — il répondrait « aucun résultat » pour une ville qui existe.
   Le script va donc chercher les pages restantes (API de rendu de section) dès
   que la SECTION approche du champ de vision — et non au premier contact avec
   la recherche, qui n'est plus qu'un filet —, les ajoute à la liste, puis masque
   la pagination. Si ce chargement échoue, la pagination reste en place : on
   retombe exactement sur le comportement sans JavaScript.

   Le nom du paramètre de page n'est pas deviné — Shopify le suffixe par l'id de
   la page (`page_37009733`) — il est relu dans les liens de pagination rendus.

   Idempotent (marquage `data-ak-magasins-bound`) : l'éditeur de thème réinjecte
   les sections sans recharger la page.
   ========================================================================== */

(() => {
  'use strict';

  /** Minuscules, sans accents, ponctuation réduite à des espaces. */
  const norm = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  /** Échappe ce qui part dans du HTML écrit à la main : bulles de carte, suggestions. */
  const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /** Le texte d'un morceau de fiche, vide plutôt qu'une exception s'il n'existe pas. */
  const txt = (card, sel) => {
    const el = card.querySelector(sel);
    return el ? el.textContent.trim() : '';
  };

  /** La ville seule : le gabarit loge le code postal dans le même bloc qu'elle. */
  const villeSansCp = (card) => txt(card, '.ak-mag__city').replace(txt(card, '.ak-mag__zip'), '').trim();

  /* --- Le pays dans la langue de la page ----------------------------------------
     Les revendeurs de la base portent leur pays en français (« Allemagne »), ceux
     saisis dans l'éditeur le portent traduit (« Belgien » sur /de). Le sélecteur
     y voyait deux pays, « Belgien » et « Belgique », et filtrer sur l'un masquait
     les revendeurs de l'autre (relevé du 14/09). Chaque nom est ramené à son code
     ISO, puis écrit dans la langue de la page par le navigateur : une seule entrée
     par pays, et aucune liste de traductions à tenir. Un nom inconnu reste tel
     quel. Les clés sont normalisées par `norm`, en français, anglais et allemand. */
  const CODES_PAYS = {
    allemagne: 'DE', germany: 'DE', deutschland: 'DE',
    andorre: 'AD', andorra: 'AD',
    australie: 'AU', australia: 'AU', australien: 'AU',
    autriche: 'AT', austria: 'AT', osterreich: 'AT',
    belgique: 'BE', belgium: 'BE', belgien: 'BE',
    canada: 'CA', kanada: 'CA',
    'coree du sud': 'KR', 'south korea': 'KR', sudkorea: 'KR',
    danemark: 'DK', denmark: 'DK',
    espagne: 'ES', spain: 'ES', spanien: 'ES',
    france: 'FR', frankreich: 'FR',
    groenland: 'GL', greenland: 'GL', gronland: 'GL',
    irlande: 'IE', ireland: 'IE', irland: 'IE',
    italie: 'IT', italy: 'IT', italien: 'IT',
    japon: 'JP', japan: 'JP',
    'la reunion': 'RE', reunion: 'RE',
    lituanie: 'LT', lithuania: 'LT', litauen: 'LT',
    luxembourg: 'LU', luxemburg: 'LU',
    martinique: 'MQ',
    'nouvelle zelande': 'NZ', 'new zealand': 'NZ', neuseeland: 'NZ',
    'pays bas': 'NL', netherlands: 'NL', niederlande: 'NL',
    pologne: 'PL', poland: 'PL', polen: 'PL',
    'royaume uni': 'GB', 'united kingdom': 'GB', 'vereinigtes konigreich': 'GB',
    'saint pierre et miquelon': 'PM', 'st pierre miquelon': 'PM', 'st pierre und miquelon': 'PM',
    singapour: 'SG', singapore: 'SG', singapur: 'SG',
    suisse: 'CH', switzerland: 'CH', schweiz: 'CH',
    tchequie: 'CZ', czechia: 'CZ', tschechien: 'CZ',
    turquie: 'TR', turkey: 'TR', turkiye: 'TR', turkei: 'TR',
    'etats unis': 'US', 'united states': 'US', 'vereinigte staaten': 'US',
  };
  const LANGUE = document.documentElement.lang || 'fr';
  const nomsDePays = (() => {
    try {
      return new Intl.DisplayNames([LANGUE], { type: 'region' });
    } catch (e) {
      return null;
    }
  })();

  /** Réécrit le pays des fiches dans la langue de la page, y compris dans leur texte cherchable. */
  const localiserPays = (liste) => {
    if (!nomsDePays) return;
    for (const card of liste) {
      const code = CODES_PAYS[norm(card.dataset.akMagCountry)];
      const nom = code && nomsDePays.of(code);
      if (!nom || nom === code || nom === card.dataset.akMagCountry) continue;
      card.dataset.akMagCountry = nom;
      const affiche = card.querySelector('.ak-mag__country');
      if (affiche) affiche.textContent = nom;
      // « Deutschland » doit trouver les revendeurs allemands ; le nom d'origine reste cherchable.
      card.dataset.akMagSearch = `${card.dataset.akMagSearch || ''} ${nom}`.trim();
    }
  };

  function initMagasins(root = document) {
    const scopes = root.querySelectorAll('[data-ak-magasins]:not([data-ak-magasins-bound])');

    scopes.forEach((scope) => {
      const form = scope.querySelector('[data-ak-mag-form]');
      const list = scope.querySelector('[data-ak-mag-list]');
      let cards = Array.from(scope.querySelectorAll('[data-ak-mag]'));
      // Le formulaire n'est plus exigé : « Afficher la carte » sans « Afficher la
      // recherche » est un réglage valable, et il ne donnait rien du tout.
      if (!cards.length) return;

      scope.dataset.akMagasinsBound = '1';

      const input = scope.querySelector('[data-ak-mag-search]');
      const country = scope.querySelector('[data-ak-mag-country]');
      const counter = scope.querySelector('[data-ak-mag-count]');
      const empty = scope.querySelector('[data-ak-mag-empty]');
      const prompt = scope.querySelector('[data-ak-mag-prompt]');
      const pagesNav = scope.querySelector('[data-ak-mag-pages]');
      const resets = Array.from(scope.querySelectorAll('[data-ak-mag-reset]'));
      // Celui de la barre n'apparaît qu'une fois un filtre posé ; celui de
      // l'état vide suit la visibilité de son bloc et n'est pas piloté ici.
      const resetBar = form ? form.querySelector('[data-ak-mag-reset]') : null;

      /* --- Réglages venus du gabarit ---------------------------------------- */

      let cfg = {};
      try {
        const el = scope.querySelector('[data-ak-mag-config]');
        if (el) cfg = JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[akinod-magasins]', e);
      }
      const totalReel = Number(cfg.total) || cards.length;
      const nbPages = Number(cfg.pages) || 1;
      // La page que le serveur vient de rendre : elle est déjà sous les yeux.
      const pageCourante = Number(cfg.page) || 1;
      // L'invitation à chercher n'a de sens que si elle existe dans le gabarit —
      // et qu'il y a un champ où chercher : masquer la liste derrière un « cherchez
      // donc » sans recherche laisserait le visiteur devant une page vide.
      const attendreRecherche = cfg.searchFirst === true && !!prompt && !!input;

      /* --- Index de recherche ----------------------------------------------- */

      // La botte de foin est normalisée une seule fois par fiche : la frappe ne
      // doit pas repayer ce coût à chaque caractère, ni le recollage des pages
      // refaire le travail déjà fait.
      const hay = new Map();
      const indexer = (liste) => {
        for (const c of liste) hay.set(c, norm(c.dataset.akMagSearch || c.textContent));
      };
      localiserPays(cards);
      indexer(cards);

      /** Une fiche répond-elle au filtre ? Le même test sert la liste et les
       *  suggestions : deux écritures, c'étaient deux occasions de diverger. */
      const correspond = (card, terms, wanted) => {
        if (wanted && card.dataset.akMagCountry !== wanted) return false;
        // Tous les mots doivent être présents : « paris droguerie » réduit,
        // il n'élargit pas.
        const text = hay.get(card);
        return terms.every((t) => text.includes(t));
      };

      /* --- Sélecteur de pays -------------------------------------------------
         Reconstruit depuis les fiches présentes : le serveur ne connaît que la
         page qu'il vient de rendre, le DOM les connaît toutes. */
      const majPays = () => {
        if (!country) return;
        const choix = country.value;
        const noms = [...new Set(cards.map((c) => c.dataset.akMagCountry).filter(Boolean))];
        noms.sort((a, b) => a.localeCompare(b, LANGUE));
        // Comparer les noms, et non leur nombre : un ensemble renouvelé sans
        // changer de taille laissait au sélecteur des pays qu'aucune fiche ne
        // porte — et filtrer dessus ne rendait rien.
        const poses = [...country.options].slice(1).map((o) => o.value);
        if (poses.length === noms.length && poses.every((n, i) => n === noms[i])) return;
        const tous = country.options[0];
        country.innerHTML = '';
        country.append(tous);
        for (const n of noms) {
          const o = document.createElement('option');
          o.value = n;
          o.textContent = n;
          country.append(o);
        }
        country.value = noms.includes(choix) ? choix : '';
      };
      majPays();

      /* --- La carte -----------------------------------------------------------
         Reprise du dispositif d'origine (Leaflet + grappes de marqueurs), servi
         depuis les assets du thème. Rien n'est téléchargé tant que la zone
         n'approche pas de l'écran, et l'échec est silencieux : sans carte, la
         page reste une liste qui se cherche. */

      const carte = (() => {
        const zone = scope.querySelector('[data-ak-mag-mapzone]');
        const cible = scope.querySelector('[data-ak-mag-map]');
        const reg = (cfg.carte || {});
        const inerte = { montrer() {}, viser() {}, amorcer() {} };
        if (!zone || !cible || reg.active !== true || !reg.tuiles) return inerte;

        let L = null, plan = null, grappes = null, icone = null;
        let attente = null;          // dernier état demandé avant que Leaflet soit là
        let chargement = null;
        const parCarte = new Map(); // fiche -> marqueur
        let visee = null;           // fiche mise en avant depuis la carte

        /** Contenu de la bulle : la même information que la fiche, en plus court. */
        const bulle = (card) => {
          const lignes = [
            txt(card, '.ak-mag__street'),
            txt(card, '.ak-mag__complement'),
            [txt(card, '.ak-mag__zip'), villeSansCp(card)].filter(Boolean).join(' '),
            txt(card, '.ak-mag__tel'),
          ].filter(Boolean);
          return `<span class="ak-mag__pop-name">${esc(txt(card, '.ak-mag__name'))}</span>` +
                 lignes.map(esc).join('<br>');
        };

        /** Met une fiche en avant et l'amène sous les yeux. */
        const pointer = (card) => {
          if (visee && visee !== card) visee.classList.remove('is-pointed');
          visee = card;
          card.classList.add('is-pointed');
          if (!card.hidden) card.scrollIntoView({ block: 'center', behavior: 'smooth' });
        };

        const marqueur = (card) => {
          let m = parCarte.get(card);
          if (m) return m;
          const lat = parseFloat(card.dataset.akMagLat);
          const lng = parseFloat(card.dataset.akMagLng);
          if (!isFinite(lat) || !isFinite(lng)) return null;
          m = L.marker([lat, lng], { icon: icone, title: txt(card, '.ak-mag__name') });
          m.bindPopup(bulle(card));
          // Cliquer un marqueur doit désigner sa fiche : sinon la carte et la
          // liste racontent deux histoires parallèles.
          m.on('click', () => pointer(card));
          parCarte.set(card, m);
          return m;
        };

        /** Le point et le zoom d'ouverture, réglés sur la section. */
        const vueOuverture = () => [
          (reg.vue && reg.vue[0]) != null ? Number(reg.vue[0]) : 46.6,
          (reg.vue && reg.vue[1]) != null ? Number(reg.vue[1]) : 2.4,
        ];

        const dessiner = (fiches, filtreActif) => {
          const marqueurs = [];
          for (const c of fiches) {
            const m = marqueur(c);
            if (m) marqueurs.push(m);
          }
          grappes.clearLayers();
          if (marqueurs.length) grappes.addLayers(marqueurs);

          if (!marqueurs.length) return;

          // Le cadrage se rejoue à chaque changement de filtre — c'est ce qui
          // fait que la carte répond à la liste.
          const plafond = reg.zoomMax || 13;
          const bornes = L.latLngBounds(marqueurs.map((m) => m.getLatLng()));
          const tiendrait = plan.getBoundsZoom(bornes, false);

          // Une recherche peut ramener des résultats aux quatre coins du monde :
          // « paris » trouve le Marais ET les rues de Paris de La Réunion. Tout
          // cadrer donnerait un planisphère, c'est-à-dire rien. Passé un certain
          // éparpillement on se pose sur le premier résultat, comme le faisait
          // la page d'origine ; sinon on cadre l'ensemble, ce qui reste plus
          // parlant pour un pays entier.
          if (!filtreActif) {
            /* Rien de cherché : on se pose sur le cadrage d'ouverture plutôt que
               sur l'ensemble des marqueurs. Cadrer les 28 pays donnait un
               planisphère où la France n'était qu'une grappe, et la carte
               paraissait plus pauvre que celle du site historique alors qu'elle
               porte les mêmes adresses (retour AUT-001). */
            plan.setView(vueOuverture(), (reg.vue && reg.vue[2]) || 6);
          } else if (tiendrait < 5) {
            plan.setView(marqueurs[0].getLatLng(), plafond);
          } else {
            plan.fitBounds(bornes, { padding: [28, 28], maxZoom: plafond });
          }
        };

        /** Charge une feuille de style et attend qu'elle soit posée. */
        const css = (href) => new Promise((ok) => {
          const l = document.createElement('link');
          l.rel = 'stylesheet';
          l.href = href;
          l.onload = l.onerror = () => ok();
          document.head.append(l);
        });

        const js = (src) => new Promise((ok, ko) => {
          const s = document.createElement('script');
          s.src = src;
          s.onload = ok;
          s.onerror = () => ko(new Error(`script illisible : ${src}`));
          document.head.append(s);
        });

        const demarrer = async () => {
          await Promise.all([css(reg.leafletCss), css(reg.grappesCss)]);
          await js(reg.leafletJs);        // les grappes étendent Leaflet : l'ordre compte
          await js(reg.grappesJs);
          L = window.L;
          if (!L || !L.markerClusterGroup) throw new Error('Leaflet absent après chargement');

          // Leaflet 0.7 déduit le chemin de ses icônes du dossier du script ;
          // sur le CDN Shopify les assets sont à plat, d'où l'icône explicite.
          icone = L.icon({
            iconUrl: reg.icone,
            iconRetinaUrl: reg.icone2x,
            shadowUrl: reg.ombre,
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
          });

          zone.classList.add('is-ready');
          // `scrollWheelZoom` désactivé : sur une page qui se parcourt, une
          // molette qui zoome la carte au lieu de faire défiler piège le lecteur.
          plan = L.map(cible, { scrollWheelZoom: false }).setView(vueOuverture(), (reg.vue && reg.vue[2]) || 6);
          L.tileLayer(reg.tuiles, {
            // Obligation de licence : jamais masquée, jamais optionnelle.
            attribution: reg.attribution || '',
            maxZoom: 19,
          }).addTo(plan);
          // Ce préfixe varie d'une édition de Leaflet à l'autre et redoublait ici
          // le nom du fournisseur ; on le fixe sur le crédit de la bibliothèque.
          if (plan.attributionControl) {
            plan.attributionControl.setPrefix('<a href="https://leafletjs.com">Leaflet</a>');
          }

          grappes = L.markerClusterGroup({
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: true,
            zoomToBoundsOnClick: true,
            maxClusterRadius: 35,
          });
          plan.addLayer(grappes);

          // Le bouton « Voir sur la carte » n'a de sens qu'ici : il reste masqué
          // partout ailleurs plutôt que de promettre une action impossible.
          // Un drapeau sur la section, et non un balayage des boutons : les
          // fiches recollées ensuite doivent en hériter sans qu'on y revienne.
          scope.classList.add('is-mapped');

          if (attente) dessiner(attente.fiches, attente.filtreActif);
          attente = null;
        };

        const amorcer = () => {
          if (chargement) return chargement;
          chargement = demarrer().catch((e) => {
            // Sans carte, la page reste utilisable : on ne laisse pas un cadre
            // vide, on n'affiche rien.
            console.warn('[akinod-magasins]', e);
            zone.classList.remove('is-ready');
          });
          return chargement;
        };

        return {
          amorcer,
          montrer(fiches, filtreActif) {
            if (!plan) { attente = { fiches, filtreActif }; return; }
            dessiner(fiches, filtreActif);
          },
          viser(card) {
            const lat = parseFloat(card.dataset.akMagLat);
            const lng = parseFloat(card.dataset.akMagLng);
            if (!plan || !isFinite(lat) || !isFinite(lng)) return;
            plan.setView([lat, lng], reg.zoomFiche || 15);
            const m = parCarte.get(card);
            // `zoomToShowLayer` déplie la grappe qui contient le marqueur :
            // sans lui, la bulle s'ouvrirait sur un point encore groupé.
            if (m) grappes.zoomToShowLayer(m, () => m.openPopup());
            zone.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          },
        };
      })();

      /* --- Filtrage ---------------------------------------------------------- */

      let recolle = nbPages <= 1;

      const apply = () => {
        const terms = norm(input ? input.value : '').split(' ').filter(Boolean);
        const wanted = country ? country.value : '';
        const active = terms.length > 0 || wanted !== '';
        // Tant qu'aucun filtre n'est posé, mille adresses ne se lisent pas :
        // on invite à chercher plutôt que de dérouler l'annuaire entier.
        const enAttente = attendreRecherche && !active;
        let shown = 0;

        // La carte suit le filtre, pas l'invitation : même quand la liste attend
        // une saisie, tous les revendeurs sont visibles sur la carte. C'est
        // justement ce qu'on vient y chercher.
        const retenues = [];

        for (const card of cards) {
          const match = correspond(card, terms, wanted);

          card.hidden = enAttente || !match;
          if (match) {
            shown += 1;
            retenues.push(card);
            // Une fiche filtrée peut n'avoir jamais croisé le champ de vision :
            // on la montre franchement plutôt que de la laisser transparente.
            if (active) card.classList.add('is-visible');
          }
        }

        if (counter) {
          // Sans filtre, on annonce le total réel — celui de la base, pas celui
          // des fiches déjà téléchargées.
          const n = active ? shown : totalReel;
          const word = n > 1 ? counter.dataset.many : counter.dataset.one;
          counter.textContent = `${n} ${word || ''}`.trim();
        }

        if (prompt) prompt.hidden = !enAttente;
        if (empty) empty.hidden = enAttente || shown > 0;
        if (resetBar) resetBar.hidden = !active;
        // La pagination du serveur ne sert plus une fois tout recollé, et n'a
        // rien à dire sous une liste volontairement masquée.
        if (pagesNav) pagesNav.hidden = recolle || enAttente;

        carte.montrer(retenues, active);
      };

      /* --- Recollage des pages restantes -------------------------------------- */

      /** Nom du paramètre de page, relu dans les liens rendus par Shopify. */
      const paramPage = () => {
        const lien = pagesNav && pagesNav.querySelector('a[href]');
        if (lien) {
          for (const cle of new URL(lien.href, location.origin).searchParams.keys()) {
            if (cle.indexOf('page') === 0) return cle;
          }
        }
        return 'page';
      };

      let recollage = null;
      const recoller = () => {
        if (recolle || recollage || !list || !cfg.sectionId) return recollage;
        const cle = paramPage();

        /** Une page de section, rendue seule par Shopify. */
        const chercherPage = async (p) => {
          const url = `${location.pathname}?section_id=${encodeURIComponent(cfg.sectionId)}` +
                      `&${encodeURIComponent(cle)}=${p}`;
          const res = await fetch(url, { headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error(`page ${p} : HTTP ${res.status}`);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          return Array.from(doc.querySelectorAll('[data-ak-mag]'));
        };

        recollage = (async () => {
          // Toutes les pages sauf celle qui est déjà là. Demander « 2 à N » quelle
          // que soit la page courante doublait la page 3 pour qui arrivait par
          // ?page=3, et ne rapatriait jamais la première — celle qui porte les
          // adresses saisies dans l'éditeur.
          const aLire = [];
          for (let p = 1; p <= nbPages; p += 1) {
            if (p !== pageCourante) aLire.push(p);
          }

          // Par groupes de quatre : en série, une liste réglée sur 50 fiches par
          // page demanderait vingt-quatre allers-retours à la file. En parallèle
          // sans limite, on ouvrirait autant de connexions d'un coup.
          const lues = new Map();
          const file = aLire.slice();
          while (file.length) {
            const groupe = file.splice(0, 4);
            const lots = await Promise.all(groupe.map(chercherPage));
            groupe.forEach((p, i) => lues.set(p, lots[i]));
          }

          // Deux points d'insertion, pas un par fiche : les pages d'avant, les
          // pages d'après. Le navigateur ne recalcule la mise en page qu'une fois
          // par paquet, et la liste garde l'ordre du serveur — blocs d'éditeur en
          // tête, puisqu'ils ouvrent la première page.
          const avant = document.createDocumentFragment();
          const apres = document.createDocumentFragment();
          const ajoutees = [];
          for (const p of aLire) {
            const paquet = p < pageCourante ? avant : apres;
            for (const c of lues.get(p)) {
              const noeud = document.importNode(c, true);
              paquet.append(noeud);
              ajoutees.push(noeud);
            }
          }
          if (avant.childNodes.length) list.prepend(avant);
          if (apres.childNodes.length) list.append(apres);

          // `cards` reprend l'ordre du DOM : c'est celui que suivent les
          // suggestions et le cadrage de la carte.
          cards = Array.from(list.querySelectorAll('[data-ak-mag]'));
          localiserPays(ajoutees);
          indexer(ajoutees);
          recolle = true;
          majPays();
          apply();
        })().catch((e) => {
          // Échec : on laisse la pagination du serveur en place, elle reste le
          // chemin vers les autres revendeurs.
          console.warn('[akinod-magasins]', e);
          recollage = null;
        });

        return recollage;
      };

      /* --- Suggestions ---------------------------------------------------------
         Reprise de l'interaction de `locations.js` : une liste sous le champ, la
         portion tapée en gras, un clic qui recentre la carte. La différence est
         la source — l'original interrogeait un service de géocodage, on propose
         ici les revendeurs eux-mêmes, ce qui évite un tiers et répond mieux à la
         question posée (« où puis-je en acheter ? »). */

      const suggest = scope.querySelector('[data-ak-mag-suggest]');
      const combo = scope.querySelector('[data-ak-mag-combo]');
      const MAX_SUG = 6;

      const fermerSug = () => {
        if (!suggest) return;
        suggest.hidden = true;
        suggest.innerHTML = '';
        if (combo) combo.setAttribute('aria-expanded', 'false');
      };

      /** Entoure de `b` la portion tapée, avec le repli de l'original : si la
       *  saisie complète ne se retrouve pas telle quelle, on réessaie sans les
       *  chiffres (« 26 rue des Écoles » surligne alors « rue des Écoles »). */
      const surligner = (texte, saisie) => {
        // La recherche porte sur le texte BRUT, et chaque morceau n'est échappé
        // qu'une fois découpé. Chercher dans le texte déjà échappé faisait tomber
        // la saisie au milieu des entités : « am », « mp » ou « amp » coupaient
        // `&amp;` en deux et affichaient « Barbecue &amp;amp; Co ». Quatre-vingt-sept
        // raisons sociales du flux portent une esperluette.
        const poser = (q) => {
          if (!q) return null;
          const motif = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
          let sortie = '';
          let fin = 0;
          let trouve = false;
          for (let m = motif.exec(texte); m; m = motif.exec(texte)) {
            trouve = true;
            sortie += `${esc(texte.slice(fin, m.index))}<b>${esc(m[0])}</b>`;
            fin = m.index + m[0].length;
          }
          return trouve ? sortie + esc(texte.slice(fin)) : null;
        };

        return poser(saisie.trim()) || poser(saisie.replace(/\d+/g, '').trim()) || esc(texte);
      };

      const ouvrirSug = () => {
        if (!suggest || !input) return;
        const saisie = input.value.trim();
        if (saisie.length < 2) return fermerSug();

        const terms = norm(saisie).split(' ').filter(Boolean);
        const wanted = country ? country.value : '';
        const vus = new Set();
        const lignes = [];

        for (const card of cards) {
          if (lignes.length >= MAX_SUG) break;
          if (!correspond(card, terms, wanted)) continue;
          const cle = norm(txt(card, '.ak-mag__name'));
          if (vus.has(cle)) continue; // une enseigne à plusieurs adresses ne se répète pas
          vus.add(cle);
          lignes.push(card);
        }

        if (!lignes.length) return fermerSug();

        suggest.innerHTML = lignes.map((card, i) => {
          const ou = [villeSansCp(card), card.dataset.akMagCountry || ''].filter(Boolean).join(', ');
          return `<li class="ak-mag__sug-item" role="option" aria-selected="false" data-i="${i}">` +
                 `${surligner(txt(card, '.ak-mag__name'), saisie)}` +
                 `<span class="ak-mag__sug-where">${surligner(ou, saisie)}</span></li>`;
        }).join('');

        suggest.hidden = false;
        if (combo) combo.setAttribute('aria-expanded', 'true');

        for (const item of suggest.querySelectorAll('.ak-mag__sug-item')) {
          item.addEventListener('mousedown', (e) => {
            // `mousedown` et non `click` : le champ perd le focus avant le clic,
            // et un `blur` qui referme la liste emporterait la sélection.
            e.preventDefault();
            const card = lignes[Number(item.dataset.i)];
            input.value = txt(card, '.ak-mag__name');
            fermerSug();
            apply();
            carte.viser(card);
          });
        }
      };

      const clear = () => {
        if (input) input.value = '';
        if (country) country.value = '';
        fermerSug();
        apply();
        input?.focus();
      };

      // Une frappe ne doit pas déclencher un balayage complet par caractère.
      let timer = null;
      const schedule = () => {
        clearTimeout(timer);
        timer = setTimeout(() => { apply(); ouvrirSug(); }, 120);
      };

      input?.addEventListener('blur', () => setTimeout(fermerSug, 120));
      input?.addEventListener('keydown', (e) => { if (e.key === 'Escape') fermerSug(); });

      /* --- La liste parle à la carte ------------------------------------------- */

      // Délégation : les fiches recollées après coup héritent du comportement
      // sans qu'on ait à les rebrancher une à une.
      list?.addEventListener('click', (e) => {
        const bouton = e.target.closest('[data-ak-mag-focus]');
        if (!bouton) return;
        const card = bouton.closest('[data-ak-mag]');
        if (card) carte.viser(card);
      });

      // Filet, et rien de plus : le rapatriement est déjà lancé par l'approche de
      // la section (plus bas). Ces trois écoutes couvrent le cas où l'observateur
      // n'a pas encore vu passer la section — un clavier qui saute droit au champ,
      // un navigateur sans IntersectionObserver.
      const armer = () => recoller();
      input?.addEventListener('focus', armer, { once: true });
      input?.addEventListener('input', armer, { once: true });
      country?.addEventListener('focus', armer, { once: true });

      input?.addEventListener('input', schedule);
      input?.addEventListener('search', apply); // croix native du champ `search`
      country?.addEventListener('change', () => { recoller(); apply(); });
      resets.forEach((b) => b.addEventListener('click', clear));

      // Le formulaire ne part jamais au serveur : « Entrée » applique le filtre
      // et referme le clavier mobile.
      form?.addEventListener('submit', (e) => {
        e.preventDefault();
        clearTimeout(timer);
        apply();
        input?.blur();
      });

      // Le chargement de la carte s'arme quand la section approche de l'écran,
      // comme la cinématique le fait pour son moteur 3D. C'est la SECTION qu'on
      // observe, pas la zone de carte : celle-ci reste en `display:none` tant
      // que Leaflet n'a pas répondu, et un élément sans boîte ne croise rien.
      //
      // Le recollage part dans la foulée : une carte qui n'afficherait qu'un
      // cinquième des revendeurs mentirait sur la couverture du réseau.
      const approcher = () => { carte.amorcer(); recoller(); };

      if ('IntersectionObserver' in window) {
        const veille = new IntersectionObserver((entrees) => {
          if (!entrees.some((e) => e.isIntersecting)) return;
          veille.disconnect();
          approcher();
        }, { rootMargin: '200% 0px' });
        veille.observe(scope);
      } else {
        approcher();
      }

      apply();
    });
  }

  /* --- Amorçage ------------------------------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initMagasins(document));
  } else {
    initMagasins(document);
  }

  document.addEventListener('shopify:section:load', (e) => initMagasins(e.target));
})();
